'use client'

import classNames from 'classnames'
import { useSiteInfo } from 'shared'

import { MouseEvent } from 'react'

import { useTranslation } from 'react-i18next'
import SvgIconArrowDown from 'ui/svg/icons/fill/arrow-down.svg'
import SvgSearchIcon from 'ui/svg/icons/stroke/search.svg'
import SvgHamburgerIcon from 'ui/svg/icons/fill/hamburger.svg'
import { HeaderTopNavMenuType } from 'tsconfig/craft-types'
import { AButton } from '../atoms/a-button'
import { ASvg } from '../atoms/a-svg'
import { CustomLink } from './c-the-header'

type Props = {
  headerTopNavMenu: HeaderTopNavMenuType[]
  theme?: string
  isClear?: boolean
  isClearDark?: boolean
  doctorsUrl?: string
  quizUrl?: string
  discoveryUrl?: string
  openMenu?: () => void
  handleNavigationLinkClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export const CCustomNavList = (props: Props) => {
  const {
    doctorsUrl,
    discoveryUrl,
    quizUrl,
    isClear,
    theme,
    isClearDark,
    openMenu,
    handleNavigationLinkClick,
    headerTopNavMenu,
  } = props
  const siteInfo = useSiteInfo()
  const { t } = useTranslation('common')

  const handleQuizButtonClick = () => {
    //
  }

  const handleDoctorsButtonClick = () => {
    //
  }

  return (
    <ul
      className={`flex items-center w-[100%] md:w-auto ${
        doctorsUrl ? 'justify-between' : 'justify-end'
      } md:justify-start`}
    >
      {!siteInfo.isES && (
        <>
          {headerTopNavMenu.map((topMenu, i) => (
            <li
              className={`c-the-header__nav-item hidden pis-8 xl:block relative `}
              key={i}
              style={{
                order: topMenu.linkOrder,
              }}
            >
              {topMenu?.submenu && topMenu?.submenu?.length > 0 ? (
                <span
                  className={classNames(
                    `flex align-center cursor-pointer items-center font-bold text-center uppercase transition-all duration-300 text-button align-middle text-neutral-white hover:text-neutral-light-gray`,
                    {
                      'text-neutral-white hover:text-neutral-light-gray':
                        (isClear && !isClearDark) ||
                        (!isClear && theme === 'dark'),
                      'text-neutral-white hover:text-neutral-light-gray md:text-neutral-dark-gray md:hover:text-neutral-black':
                        isClear && isClearDark,
                      'text-neutral-dark-gray hover:text-neutral-black':
                        !isClear && theme === 'light',
                    }
                  )}
                >
                  {topMenu.linkTitle}
                  <ASvg
                    className={`fill-current`}
                    svg={SvgIconArrowDown}
                    ariaHidden="true"
                  />
                  {topMenu.submenu && topMenu.submenu?.length > 0 && (
                    <ul className="c-the-header__nav-dropdown absolute top-[30px] left-0 z-30 w-full transition-all duration-300 m-dropdown text-neutral-dark-gray min-w-[200px]">
                      {topMenu.submenu.map((subItem, i) => (
                        <li
                          key={i}
                          style={{
                            order: subItem.linkOrder,
                          }}
                          className={`bg-neutral-light-gray border-neutral-gray first:border-t-[0] border-t`}
                        >
                          <CustomLink
                            className="block capitalize text-left p-4 font-semibold transition-colors duration-300 text-p hover:text-neutral-black"
                            href={subItem.linkUrl}
                            target={subItem.openNewTab ? '_blank' : '_self'}
                            onClick={handleNavigationLinkClick}
                          >
                            {subItem.linkTitle}
                          </CustomLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </span>
              ) : (
                <CustomLink
                  className={classNames(
                    `flex align-center items-center font-bold text-center uppercase transition-all duration-300 text-button align-middle text-neutral-white hover:text-neutral-light-gray`,
                    {
                      'text-neutral-white hover:text-neutral-light-gray':
                        (isClear && !isClearDark) ||
                        (!isClear && theme === 'dark'),
                      'text-neutral-white hover:text-neutral-light-gray md:text-neutral-dark-gray md:hover:text-neutral-black':
                        isClear && isClearDark,
                      'text-neutral-dark-gray hover:text-neutral-black':
                        !isClear && theme === 'light',
                    }
                  )}
                  href={topMenu.linkUrl}
                  target={topMenu.openNewTab ? '_blank' : '_self'}
                  onClick={handleNavigationLinkClick}
                >
                  <span> {topMenu.linkTitle} </span>
                  <ASvg
                    className={`fill-current ${
                      topMenu.submenu?.length === 0 && '!hidden'
                    }`}
                    svg={SvgIconArrowDown}
                    ariaHidden="true"
                  />
                </CustomLink>
              )}
            </li>
          ))}
          {!siteInfo.isUS && !siteInfo.isCA && !siteInfo.isGE && !siteInfo.isUK && quizUrl && (
            <li className="hidden pis-8 xl:block">
              <AButton
                type="a"
                href={quizUrl}
                target={quizUrl !== '/' ? '_blank' : '_self'}
                rel="noreferrer"
                style="outline"
                color={isClear || theme === 'dark' ? 'white' : 'gray'}
                onClick={handleQuizButtonClick}
              >
                <span>{t('titles.take-our-quiz')}</span>
              </AButton>
            </li>
          )}
        </>
      )}
      {doctorsUrl && !siteInfo.isSE && (
        <li className={`${!siteInfo.isUS ? 'hidden' : ''} md:pis-8 md:block`}>
          <AButton
            type="a"
            href={doctorsUrl}
            target={doctorsUrl !== '/' ? '_blank' : '_self'}
            rel="noreferrer"
            style={'solid'}
            color={'teal'}
            icon={SvgSearchIcon}
            iconClass="stroke-current w-6 h-6"
            onClick={handleDoctorsButtonClick}
            className={
              'text-[12px] md:text-[13px] rounded-[21px] !py-[0] md:!py-[6px] !px-[14px] md:!px-[22px] md:!rounded-[20px] text-[#30B2C0] md:text-neutral-white bg-[#E9F6F7] md:bg-primary-teal border-[2px] border-teal md:!border-none h-[40px] md:h-auto'
            }
          >
            <span>{t('titles.find-a-doctor')}</span>
          </AButton>
        </li>
      )}
      <li className="mis-2 md:mis-6 xl:hidden">
        <button
          className={classNames(
            'rounded-full p-[6px] inline-flex items-center justify-center uppercase transition-all duration-300 align-middle border-2',
            {
              'border-neutral-white text-neutral-white hover:bg-neutral-white hover:text-neutral-dark-gray':
                (isClear && !isClearDark) || (!isClear && theme === 'dark'),
              'border-neutral-white text-neutral-white hover:bg-neutral-white hover:text-neutral-dark-gray md:border-neutral-dark-gray md:text-neutral-dark-gray md:hover:bg-neutral-dark-gray md:hover:text-neutral-white':
                isClear && isClearDark,
              'border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-neutral-white':
                !isClear && theme === 'light',
            }
          )}
          onClick={openMenu}
        >
          <ASvg
            className="fill-current w-6 h-6"
            svg={SvgHamburgerIcon}
            aria-label={t('hamburger')}
          />
        </button>
      </li>
    </ul>
  )
}
